import React, { useState, useEffect } from 'react';
import { Typography, Box, Button, Grid, Checkbox, TextField, FormControlLabel, Stack, Link } from '@mui/material';
import { usePostRentMutation, usePauseCallMutation, useResumeCallMutation } from '../../api/endpoints/apiConfig';
import { getApiErrorMessage } from '../../api/getApiErrorMessage';
import { AircallNofity } from '../../components/Common/notify';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import CircularProgress from '@mui/material/CircularProgress';
import { toast } from "react-toastify";
import { addId } from '../../store/reducers/aircallTagSlice';
import { useAppDispatch } from '../../hooks/useAppDispatch';
import { useLookupActiveAircallCall } from '../../hooks/useLookupActiveAircallCall';
import { getActiveCallsId } from '../../utils/callsIdSessionSync';
import dayjs from 'dayjs';

const PAUSE_NO_CALL_ID_ERROR =
    'Not able to pause the call recording. No callId found in session storage.';
type Props = {
    rentData: any;
    setrentData: React.Dispatch<React.SetStateAction<any>>;
    FacilityId: number; // Incoming facility id
    merchandaisedata:any;
    cost:any;
    periods: number;
    onPeriodsValidationError?: () => void;
    /** When set, called so the parent can show the phone field error and scroll to Customer Information */
    onCustomerPhoneInvalid?: () => void;
};
const PERIODS_BLOCK_ERROR_MESSAGE = 'Error - Payment Periiods must be 0 to move-in.';
const TakePaymentAndRent: React.FC<Props> = ({ cost,rentData, setrentData, FacilityId,merchandaisedata, periods, onPeriodsValidationError, onCustomerPhoneInvalid}) => {
    const params = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    // Hook to dispatch actions
    const dispatch = useAppDispatch();
    const [facilityId, setFacilityId] = useState(FacilityId); // Set incoming FacilityId in state
    const [PostRent] = usePostRentMutation();
    const [iscredit, setIsCredit] = useState(false);
    const [isenableautomaticpayment, setIsenableautomaticpayment] = useState(true);
    const [loading, setLoading] = useState(false);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const promoid = params.promotionId;
    const [processing, setProcessing] = useState(false);
    const [pauseCall] = usePauseCallMutation();
    const [resumeCall] = useResumeCallMutation();
    const { lookupActiveCall } = useLookupActiveAircallCall();
    const FacilityData = sessionStorage.getItem('facilityData');
    const facilityDataObject = FacilityData ? JSON.parse(FacilityData) : null;
    const isTestFacility = facilityDataObject[0]?.is_test === 1;

    // Update facilityId state whenever the FacilityId prop changes
    useEffect(() => {
        setFacilityId(FacilityId);
        if (rentData) {
            setCreditCard({
                firstname: rentData.creditfirstname || "",
                lastname: rentData.creditlastname || "",
                CredictCardNumber: rentData.CredictCardNumber || "",
                ExpiryMonth: rentData.ExpiryMonth || "",
                ExpiryYear: rentData.ExpiryYear || "",
                CVV: rentData.CVV || "",
                card_zip: rentData.card_zip || "",
                card_type: rentData.card_type || ""
            });
        }
    }, [FacilityId, rentData]);
    // tag api call
    const tagdatasave = () => {
        // Retrieve and parse tag data from sessionStorage
        // Setting Data Read from the session storage
        const tagdata = sessionStorage.getItem('tagsdata');
        const tagdataObject = tagdata ? JSON.parse(tagdata) : {};
        const tags = tagdataObject.tags || [];
        // reservation new tags
        const Rentaldata = tags.find((tag: { name: string }) => tag.name === 'Rental');
        // Find tags based on names
        const Reservationconvert = tags.find((tag: { name: string }) => tag.name === 'Reservation - Converted');
        const IsLeaddataconvert = tags.find((tag: { name: string }) => tag.name === 'Lead - Converted');

        if (rentData.label === 'convert') {
            dispatch(addId(Rentaldata.id));
            if (rentData.type==="lead") {
                dispatch(addId(IsLeaddataconvert.id));
            } else {
                dispatch(addId(Reservationconvert.id));
            }
        }
        if (rentData.label === 'New') {
            dispatch(addId(Rentaldata.id)); // Dispatch to Redux store
        }

        if(rentData.label === 'Update')
        {
            if (rentData.type==="lead") {
                dispatch(addId(IsLeaddataconvert.id));
            }
            else {
                dispatch(addId(Reservationconvert.id));
            }
            dispatch(addId(Rentaldata.id));
        }
    };
    const tryPauseRecording = async (callId: string): Promise<boolean> => {
        try {
            const response = await pauseCall(callId).unwrap();
            if (response.status === 'success') {
                toast.info('Call recording paused successfully');
                setIsCredit(!iscredit);
                return true;
            }
            if (response.status === 'error') {
                console.error('Pause call API error status:', response);
            } else {
                console.error('Unexpected pause call response:', response);
            }
            return false;
        } catch (error) {
            console.error('Pause call error:', error);
            return false;
        }
    };

    //payement click
    const handlePayment = async () => {
        let callId = getActiveCallsId();

        if (callId) {
            const paused = await tryPauseRecording(callId);
            if (paused) {
                return;
            }
        }

        await lookupActiveCall({ showErrorToasts: false });
        callId = getActiveCallsId();

        if (!callId) {
            console.error('No callId found in session storage');
            toast.error(PAUSE_NO_CALL_ID_ERROR);
            return;
        }

        const paused = await tryPauseRecording(callId);
        if (paused) {
            return;
        }

        if (!getActiveCallsId()) {
            toast.error(PAUSE_NO_CALL_ID_ERROR);
            return;
        }
        toast.error('Failed to pause the call recording');
    };

    // State for Credit Card info
    const [CreditCard, setCreditCard] = useState({
        firstname: rentData.creditfirstname || "",
        lastname: rentData.creditlastname || "",
        CredictCardNumber: rentData.CredictCardNumber,
        ExpiryMonth: rentData.ExpiryMonth,
        ExpiryYear: rentData.ExpiryYear,
        CVV: rentData.CVV,
        card_zip: rentData.card_zip,
        card_type: rentData.card_type,
    });
    const loadTestData = () => {
        const nextYear = (new Date().getFullYear() + 1).toString().slice(-2);
        setCreditCard({
            firstname: "John",
            lastname: "Doe",
            CredictCardNumber: "4111-1111-1111-1111", // Visa test card number
            ExpiryMonth: "12",
            ExpiryYear: nextYear,
            CVV: "123",
            card_zip: "12345",
            card_type: "visa",
        });
        toast.success("Test Data is loaded");
    };

    const loadNegativeTest1 = () => {
        setCreditCard(prev => ({
            ...prev,
            firstname: "FAIL1"
        }));
        toast.info("Negative Test 1 loaded - Processor Error");
    };

    const loadNegativeTest2 = () => {
        setCreditCard(prev => ({
            ...prev,
            firstname: "FAIL2"
        }));
        toast.info("Negative Test 2 loaded - Expired Card Error");
    };

    const loadNegativeTest3 = () => {
        setCreditCard(prev => ({
            ...prev,
            firstname: "FAIL3"
        }));
        toast.info("Negative Test 3 loaded - Card Decline Error");
    };
    const loadNegativeTest4 = () => {
        setCreditCard(prev => ({
            ...prev,
            firstname: "FAIL4"
        }));
        toast.info("Negative Test 4 loaded - Network Connection Error");
    };
    const loadNegativeTest5 = () => {
        setCreditCard(prev => ({
            ...prev,
            firstname: "FAIL5"
        }));
        toast.info("Negative Test 5 loaded - Connection Reset by Peer Error");
    };
    const loadNegativeTest6 = () => {
        setCreditCard(prev => ({
            ...prev,
            firstname: "FAIL6"
        }));
        toast.info("Negative Test 6 loaded - Soft Decline: The authorization request was approved");
    };
    // Utility function to validate credit card number using Luhn's algorithm
    const validateCardNumber = (number: string) => {
        let nCheck = 0, bEven = false;
        number = number.replace(/\D/g, "");
        for (let n = number.length - 1; n >= 0; n--) {
            let cDigit = number.charAt(n);
            let nDigit = parseInt(cDigit, 10);
            if (bEven) {
                if ((nDigit *= 2) > 9) nDigit -= 9;
            }
            nCheck += nDigit;
            bEven = !bEven;
        }
        return (nCheck % 10) === 0;
    };
    // Validate expiration date
    const validateExpiry = (month: string, year: string) => {
        const currentDate = new Date();
        const currentMonth = currentDate.getMonth() + 1;
        const currentYear = currentDate.getFullYear() % 100; // Get last two digits of year
        const expMonth = parseInt(month, 10);
        const expYear = parseInt(year, 10);

       // Ensure year is valid and within range
    if (isNaN(expYear) || Number(expYear) < currentYear || Number(expYear) > 2099) {
        return false;
    }
    // Ensure month is numeric and within the range of 1 to 12
    if (isNaN(expMonth) || expMonth < 1 || expMonth > 12) {
        return false;
    }

    // Check month is valid and ensure the expiry date is valid
    return expYear > currentYear || (expYear === currentYear && expMonth >= currentMonth);
    };
    // Validate CVV based on card type
    const validateCVV = (cvv: string, cardType: string) => {
        const cvvLength = cardType === 'amex' ? 4 : 3; // Amex has 4 digits, others have 3 digits
        const cvvRegex = new RegExp(`^\\d{${cvvLength}}$`);
        return cvvRegex.test(cvv);
    }; 
    // Validate zip code
    const validateZip = (zip: string) => {
        const zipRegex = /^\d{5}(-\d{4})?$/;
        return zipRegex.test(zip);
    };
    // Validetuate card type based on the first digit of card number
    const getCardType = (number: string | null): string | undefined => {
        if (!number) {
            return 'unknown';
        }

        const firstDigit = number[0];

        if (firstDigit === '3') return 'amex';
        if (firstDigit === '4') return 'visa';
        if (firstDigit === '5') return 'mastercard';
        if (firstDigit === '6') return 'discover';
        return 'unknown';
    };

    const formatQuotedRate = (quotedRate: string | number | null | undefined): number => {
        if (typeof quotedRate === 'number') {
          // If the value is already a number, ensure it has two decimals
          return parseFloat(quotedRate.toFixed(2));
        }

        if (typeof quotedRate === 'string') {
          // If it's a string, parse it into a float and ensure two decimals
          const floatValue = parseFloat(quotedRate);
          return isNaN(floatValue) ? 0.0 : parseFloat(floatValue.toFixed(2));
        }

        // For null, undefined, or invalid input, return 0.00 as default
        return 0.0;
      };

      // Cresit card save and close method
    const handleSaveAndClose = async () => {
        const currentDate = new Date();
        const currentYear = currentDate.getFullYear();

        const { CredictCardNumber, ExpiryMonth, ExpiryYear, CVV, firstname, lastname, card_zip } = CreditCard;
        if (isNaN(ExpiryYear) || Number(ExpiryYear)+2000 < currentYear || Number(ExpiryYear)+2000 > 2099) {
            AircallNofity({ type: 'error', Msg: 'Card has expired!' });
            return false;
        }
        if (!CredictCardNumber) {
            AircallNofity({ type: 'error', Msg: 'Please enter card number!' });
            return;
        }

        // Check valid credit card number
        const cardType = getCardType(CredictCardNumber);
        if (cardType == 'unknown') {
            AircallNofity({ type: 'error', Msg: 'Error: invalid credit card number. Valid credit card numbers begin with a 3, 4, 5, or 6.' });
            return;
        }

        if (!validateCardNumber(CredictCardNumber)) {
            AircallNofity({ type: 'error', Msg: 'Invalid card number!' });
            return;
        }
        else if (firstname === "" || null) {
            AircallNofity({ type: 'error', Msg: "Please enter CreditCard Name!" });
            return;
        }
        else if (lastname === "" || null) {
            AircallNofity({ type: 'error', Msg: "Please enter CreditCard Last Name!" });
            return;
        }
        if (!validateExpiry(ExpiryMonth, ExpiryYear)) {
            AircallNofity({ type: 'error', Msg: 'Card has expired!' });
            return;
        }

        if (!validateCVV(CVV, cardType ?? "")) {
            AircallNofity({ type: 'error', Msg: 'Invalid CVV!' });
            return;
        }
        if (!validateZip(card_zip)) {
            AircallNofity({ type: 'error', Msg: 'Invalid zip code!' });
            return;
            }
        setIsCredit(false); // Hide the credit card form on save and close
        AircallNofity({ type: 'success', Msg: 'Credit card details saved!' });
        // Integrate resumeCall functionality
        const callsInfoId = sessionStorage.getItem('callsId'); // Retrieve call ID from session storage

        if (!callsInfoId) {
            //  AircallNofity({ type: 'error', Msg: 'No call ID found!' });
            return;
        }
        try {
            const response = await resumeCall(callsInfoId).unwrap();
            
            // The API always returns JSON with status: 'success' or status: 'failure'
            if (response.status === 'success') {
                AircallNofity({ type: 'success', Msg: 'Call resumed successfully!' });
            } else if (response.status === 'failure') {
                // Handle failure status from API response
                AircallNofity({ type: 'error', Msg: 'Failed to resume the call. Please try again!' });
            } else {
                // Handle unexpected status
                AircallNofity({ type: 'error', Msg: 'Unexpected response from resume call API' });
                console.error('Unexpected resume call response:', response);
            }
        } catch (error) {
            // Handle network errors or other exceptions
            AircallNofity({ type: 'error', Msg: 'Failed to resume the call. Please try again!' });
            console.error('Resume call error:', error);
        }
    };
    
    // Email validation helper function
    const isValidEmail = (email: string): boolean => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };
    
    const saveReservation = async () => {
        if (periods > 0) {
            onPeriodsValidationError?.();
            AircallNofity({ type: 'error', Msg: PERIODS_BLOCK_ERROR_MESSAGE });
            return;
        }

        let savesuccess = true;
      const Expireyearconvert = Number(CreditCard.ExpiryYear)+2000;
        const agentName = sessionStorage.getItem('FirstName') + ' ' + sessionStorage.getItem('LastName');
        const { ProperityBeingStored, Otherproperitys } = rentData;
        const dataToSend =
            Otherproperitys?.trim()
            ? `${ProperityBeingStored} - ${Otherproperitys}`
            : ProperityBeingStored;
        const rented = {
            external_unit_id: rentData.ExternalUnitId.toString(),
            payment_amount: rentData.Move_in_cost,
            start_date: dayjs(rentData.start_date).format('YYYY-MM-DD'),
            end_date: null,
            is_promotion_deferred: rentData.is_promo_deferred,
            external_occupant_id: rentData.occupancyid !== "" ? rentData.occupancyid : null,
            external_deal_id: rentData.ExternalDealId ? rentData.ExternalDealId : null,
            // Omit TPP when product is not insurable or Decline cleared prodection_external_id
            external_tpp_id:
                rentData.is_insurable !== false && rentData.prodection_external_id
                    ? rentData.prodection_external_id
                    : null,
            external_promotion_id: rentData.selectedMonth === 'No Promo' ? null :rentData.promoid,
            external_fee_ids: rentData.External_fee_id.split(','),
            merchandise: merchandaisedata?merchandaisedata:[],
            cc_type: getCardType(CreditCard.CredictCardNumber),
            cc_number: CreditCard.CredictCardNumber,
            cc_cvv: CreditCard.CVV,
            cc_exp_month: CreditCard.ExpiryMonth,
            cc_exp_year:Expireyearconvert.toString(),
            billing_first_name: CreditCard.firstname,
            billing_last_name: CreditCard.lastname,
            billing_address1: rentData.customerinfoaddress1,
            billing_address2: rentData.customerinfoaddress2,
            billing_city: rentData.customerinfocity,
            billing_state: rentData.customerinfostate,
            billing_zip: CreditCard.card_zip,
            first_name: rentData.customerinfofirstname,
            last_name: rentData.customerinfolastname,
            address1: rentData.customerinfoaddress1,
            address2: rentData.customerinfoaddress2,
            city: rentData.customerinfocity,
            state: rentData.customerinfostate,
            zip: rentData.customerinfoPostalcode,
            email: rentData.customerinfoemail,
            phone: rentData.customerinfophone,
            phone_alt: "", // Make sure this is the intended value
            autopay: isenableautomaticpayment,
            customer_source: "Store Main Number",
            quoted_rate:formatQuotedRate(rentData.quoted_rate),
            vehicle_storage: false,
            id_number: rentData.IdTNumber,
            id_type: rentData.IdType,
            id_state: rentData.IdIssuer,
            is_business: false,
            business_name: "",
            source: "call-center",
            is_gift_card_eligible: false,
            agent: agentName,
            military_status: rentData.militaryStatus,
            military_branch: rentData.militaryStatusbranch ? rentData.militaryStatusbranch : null,
            military_id: rentData.militaryStatusidnumber ? rentData.militaryStatusidnumber : null,
            storage_contents: dataToSend,
            is_occupant_tpp: rentData.selectinsurence === "1" ? true : false,
            occupant_tpp_description: rentData.selectinsurence === "1" ? rentData.Description : null,
            occupant_tpp_policy_number: rentData.selectinsurence === "1" ? rentData.PolicyNumber : null,
            occupant_tpp_start_date: rentData.selectinsurence === "1" ? rentData.PolicyStartDate : null,
            occupant_tpp_end_date: rentData.selectinsurence === "1" ? rentData.PolicyEndDate : null,
            occupant_tpp_coverage_amount: rentData.selectinsurence === "1" ? rentData.CoverageAmt : null,
        };
        if (rentData.customerinfoemail === '') {
            AircallNofity({ type: 'error', Msg: 'Please enter email address!' });
            savesuccess = false
        }
        else if (!isValidEmail(rentData.customerinfoemail)) {
            AircallNofity({ type: 'error', Msg: 'Please enter a valid email address!' });
            savesuccess = false
        }
        else if (
            rentData.customerinfophone.trim() === '' ||
            rentData.customerinfophone.replace(/\D/g, '').length !== 10
        ) {
            AircallNofity({ type: 'error', Msg: 'Please enter a valid 10-digit phone number!' });
            savesuccess = false;
            onCustomerPhoneInvalid?.();
        }
        else if (rentData.customerinfolastname === "") {
            AircallNofity({ type: 'error', Msg: 'Please enter last name!' });
            savesuccess = false
        }
        else if (rentData.customerinfofirstname === "") {
            AircallNofity({ type: 'error', Msg: 'Please enter first name!' });
            savesuccess = false
        }
        else if (rentData.customerinfoaddress1 === "") {
            AircallNofity({ type: 'error', Msg: 'Please enter address!' });
            savesuccess = false
        }
        else if (rentData.customerinfocity === "") {
            AircallNofity({ type: 'error', Msg: 'Please enter city!' });
            savesuccess = false
        }
        else if (rentData.customerinfostate === "") {
            AircallNofity({ type: 'error', Msg: 'Please enter state!' });
            savesuccess = false
        }
        else if (rentData.ProperityBeingStored.length ===0) {
            AircallNofity({ type: 'error', Msg: 'Please choose Property Being Stored' });
            savesuccess = false
        }
        else if (rentData.customerinfoPostalcode === "") {
            AircallNofity({ type: 'error', Msg: "Please enter postal code!" });
            savesuccess = false
        }
        else if (rentData.IdTNumber === "") {
            AircallNofity({ type: 'error', Msg: "Please enter IdNumber!" });
            savesuccess = false
        }
        else if (rentData.IdType === "") {
            AircallNofity({ type: 'error', Msg: "Please enter IdType !" });
            savesuccess = false
        }
        else if (rentData.militaryStatus === "") {
            AircallNofity({ type: 'error', Msg: "Please enter Military Status !" });
            savesuccess = false
        }
        else if (CreditCard.CredictCardNumber === "") {
            AircallNofity({ type: 'error', Msg: 'Please enter Card Number!' });
            savesuccess = false
        }
        else if (CreditCard.firstname === "") {
            AircallNofity({ type: 'error', Msg: 'Please enter  CreditCard firstname!' });
            savesuccess = false
        }
        else if (CreditCard.lastname === "") {
            AircallNofity({ type: 'error', Msg: 'Please enter CreditCard Last Name!' });
            savesuccess = false
        }
        else if (CreditCard.CVV === "") {
            AircallNofity({ type: 'error', Msg: 'Please enter CVV!' });
            savesuccess = false
        }
        else if (CreditCard.card_zip === "") {
            AircallNofity({ type: 'error', Msg: 'Please enter zip code!' });
            savesuccess = false
        }
        else if (CreditCard.ExpiryMonth === "") {
            AircallNofity({ type: 'error', Msg: 'Please enter CreditCard ExpiryMonth!' });
            savesuccess = false
        }
        else if (CreditCard.ExpiryYear === "") {
            AircallNofity({ type: 'error', Msg: 'Please enter CreditCard ExpiryYear!' });
            savesuccess = false
        }
        if (!rentData.customerinfoPostalcode ||
            (rentData.customerinfoPostalcode.length !== 5 && rentData.customerinfoPostalcode.length !== 10)) {
            AircallNofity({ type: 'error', Msg: 'Post Code value must be 5 or 9 digits' });
            savesuccess = false;
        }

        else if (rentData.militaryStatus === "4" || rentData.militaryStatus === "5") {
            if (rentData.militaryStatusidnumber === '') {
                AircallNofity({ type: 'error', Msg: "Please enter Military Status ID number!" });
                savesuccess = false
            }
            else if (rentData.militaryStatusbranch === '' || rentData.militaryStatusbranch === null) {
                AircallNofity({ type: 'error', Msg: "Please enter Military Status Branch!" });
                savesuccess = false
            }

        }

        else if (rentData.IdType !== "passport") {
            if (rentData.IdIssuer === "") {
                AircallNofity({ type: 'error', Msg: "Please enter IdIssuer!" });
                savesuccess = false
            }
        }

        try {
            if (savesuccess) {
                setLoading(true);
                setProcessing(true);

                try {
                    const result = await PostRent({ rented, facilityId }).unwrap();

                    console.log('move-in result', result);
                    if (result.external_ledger_id) {
                        // Show success toast notification
                        AircallNofity({ type: 'success', Msg: 'Rent is successfully registered!' });

                        // Store the external ledger ID in session storage
                        const externalLedgerId = result.external_ledger_id;
                        sessionStorage.setItem('externalLedgerId', externalLedgerId);

                        const esignURL = result.esign_url;
                        sessionStorage.setItem('esignURL', esignURL);

                        const signaturePackageToken = result.signature_package_token;
                        sessionStorage.setItem('signaturePackageToken', signaturePackageToken);

                        const signaturePackageId = result.signature_package_id;
                        sessionStorage.setItem('signaturePackageId', signaturePackageId);

                        // Redirect to RentalConfirmation.tsx
                        navigate('/RentalConfirmation', { state: { rented, cost, facilityId, rentData, result,merchandaisedata,signaturePackageId } });

                        try {
                            // Try tagging the data
                            tagdatasave();
                        } catch (taggingError) {
                            console.error('Tagging Aircall failed:', taggingError);
                            AircallNofity({
                                type: 'error',
                                Msg: 'The move-in was successful, but tagging Aircall failed. Please ensure the TAG exists in AirCall.'
                            });
                        }
                    } else {
                        AircallNofity({
                            type: 'error',
                            Msg: 'Move-in failed. The FMS did not return a ledger ID.'
                        });
                    }
                } catch (postRentError) {
                    console.error('PostRent failed:', postRentError);
                    const detail =
                        getApiErrorMessage(postRentError) ??
                        'Move-in failed. Please try again.';
                    AircallNofity({
                        type: 'error',
                        Msg: detail,
                    });
                }
            }
        } catch (error) {
            if (error instanceof Error) {
                // Narrow the error to the Error type
                console.error('Unexpected failure during rent process:', error);
                AircallNofity({
                    type: 'error',
                    Msg: `Move-in failed. ${error.message}`
                });
            }
        } finally {
            setLoading(false);
            setProcessing(false);
        }


    };
    const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { checked } = event.target;
        setIsenableautomaticpayment(checked);
    };
    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        const inputElement = e.target as HTMLInputElement;
        const cursorPosition = inputElement.selectionStart || 0; // Store cursor position before change

        if (name === "CredictCardNumber") {
            let formattedValue = value.replace(/\D/g, ''); // Remove non-numeric characters

            if (formattedValue.length > 16) {
                formattedValue = formattedValue.slice(0, 16); // Limit to 16 digits
            }

            // Format with dashes after every 4 digits
            let newFormattedValue = formattedValue
                .replace(/(\d{4})/g, '$1-')
                .replace(/-$/, ''); // Remove trailing dash if present

            // Adjust cursor position
            let newCursorPosition = cursorPosition;
            if (value.length < newFormattedValue.length) {
                newCursorPosition += 1; // Move forward when adding a dash
            } else if (value.length > newFormattedValue.length) {
                newCursorPosition -= 1; // Move backward when removing a character
            }

            setCreditCard((prev) => ({ ...prev, [name]: newFormattedValue }));

            // Restore cursor position after re-render
            setTimeout(() => {
                inputElement.setSelectionRange(newCursorPosition, newCursorPosition);
            }, 0);
    }
        else if (name === "ExpiryMonth") {
            // Ensure value is numeric and limit to 2 characters
            let formattedValue = value.replace(/\D/g, ''); // Remove non-numeric characters
            if (formattedValue.length > 2) {
                formattedValue = formattedValue.slice(0, 2); // Limit to 2 digits
            }
            setCreditCard({ ...CreditCard, [name]: formattedValue });
        }
        else if (name === "ExpiryYear") {
            // Ensure value is numeric and limit to 2 characters
            let formattedValue = value.replace(/\D/g, ''); // Remove non-numeric characters
            if (formattedValue.length > 2) {
                formattedValue = formattedValue.slice(0, 2); // Limit to 2 digits
            }
            setCreditCard({ ...CreditCard, [name]: formattedValue });
        }
        else if (name === "CVV") {
            // Ensure value is numeric and limit to 2 characters
            let formattedValue = value.replace(/\D/g, ''); // Remove non-numeric characters
            if (formattedValue.length > 4) {
                formattedValue = formattedValue.slice(0, 2); // Limit to 2 digits
            }
            setCreditCard({ ...CreditCard, [name]: formattedValue });
        }
        else if (name === "card_zip") {
            // Allow for extended ZIP code format (12345-0023)
            let formattedValue = value.replace(/[^\d-]/g, ''); // Remove non-numeric and non-hyphen characters
            
            // Handle the formatting
            if (formattedValue.length > 5 && !formattedValue.includes('-')) {
                formattedValue = formattedValue.slice(0, 5) + '-' + formattedValue.slice(5, 9);
            }
            
            // Limit total length to 10 characters (5 digits + hyphen + 4 digits)
            if (formattedValue.length > 10) {
                formattedValue = formattedValue.slice(0, 10);
            }
            
            setCreditCard({ ...CreditCard, [name]: formattedValue });
        }
        else {
            setCreditCard({ ...CreditCard, [name]: value });
        }
    };
    const isNonProduction = window.config.appEnv !== 'production';
    return (
        <>
            {!iscredit && (<Box my={2} textAlign="center">
                <Button variant="contained" color="primary" onClick={handlePayment} disabled={processing}>
                    Collect Payment Details
                </Button>
            </Box>)}
            {!iscredit && (
                <Box my={2} textAlign="center">
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={isenableautomaticpayment}
                                onChange={handleCheckboxChange}
                                name="isenableautomaticpayment"
                                disabled={processing}
                            />
                        }
                        label="Enable automatic monthly payment"
                    />
                </Box>
            )}
            {iscredit && (
                <Box component="div">
                    <Box mb={2} p={2} bgcolor="#F6FBFF" border="1px solid" borderColor="#AEC6F8" borderRadius={2}>
                        <Typography mb={1} variant="h6" color="text.secondary" fontWeight={700}>
                            Credit Card Information
                        </Typography>
                        <Typography>All fields are required. We accept these credit cards: Visa, Master Card, American Express, Discover</Typography>

                        <Grid container spacing={2} my={3}>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    sx={{ backgroundColor: 'common.white' }}
                                    value={CreditCard.firstname}
                                    onChange={handleChange}
                                    name="firstname"
                                    label="First Name"
                                    variant="outlined"
                                    fullWidth
                                    size="small"
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    sx={{ backgroundColor: 'common.white' }}
                                    value={CreditCard.lastname}
                                    onChange={handleChange}
                                    name="lastname"
                                    label="Last Name"
                                    variant="outlined"
                                    fullWidth
                                    size="small"
                                />
                            </Grid>
                        </Grid>

                        <Grid container spacing={2} my={3}>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    sx={{ backgroundColor: 'common.white' }}
                                    value={CreditCard.CredictCardNumber}
                                    onChange={handleChange}
                                    name="CredictCardNumber"
                                    label="Credit Card Number"
                                    variant="outlined"
                                    fullWidth
                                    size="small"
                                    data-mask-sensitive={true}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <TextField
                                    sx={{ backgroundColor: 'common.white' }}
                                    value={CreditCard.ExpiryMonth}
                                    onChange={handleChange}
                                    name="ExpiryMonth"
                                    label="Expiry Month(MM)"
                                    variant="outlined"
                                    fullWidth
                                    size="small"
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <TextField
                                    sx={{ backgroundColor: 'common.white' }}
                                    value={CreditCard.ExpiryYear}
                                    onChange={handleChange}
                                    name="ExpiryYear"
                                    label="Expiry Year(YY)"
                                    variant="outlined"
                                    fullWidth
                                    size="small"
                                />
                            </Grid>
                        </Grid>
                        <Grid container spacing={2} my={3}>
                            <Grid item xs={12} md={2}>
                                <TextField
                                    sx={{ backgroundColor: 'common.white' }}
                                    value={CreditCard.CVV}
                                    onChange={handleChange}
                                    name="CVV"
                                    label="CVV"
                                    variant="outlined"
                                    fullWidth
                                    size="small"
                                    data-mask-sensitive={true}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <TextField
                                    sx={{ backgroundColor: 'common.white' }}
                                    value={CreditCard.card_zip}
                                    onChange={handleChange}
                                    name="card_zip"
                                    label="Zip Code"
                                    variant="outlined"
                                    fullWidth
                                    size="small"
                                    data-mask-sensitive={true}
                                />
                            </Grid>
                            <Grid item xs={12} md={2}>
                                <TextField
                                    sx={{ backgroundColor: 'common.white' }}
                                    value='US'
                                    disabled                                 
                                    name="card_country"
                                    label="Country"
                                    variant="outlined"
                                    fullWidth
                                    size="small"
                                    data-mask-sensitive={true}
                                />
                            </Grid>
                        </Grid>

                        <Box textAlign="center">
                            <Button variant="outlined" color="primary" onClick={handleSaveAndClose}>
                                Save And Close
                            </Button>
                            {(isNonProduction || isTestFacility) && (
                                <>
                                    <Link
                                        sx={{ color: '#2400cb', cursor: 'pointer', ml: 4, mr: 2, fontStyle: 'italic' }}
                                        onClick={() => loadTestData()}
                                    >
                                        Load Valid Data
                                    </Link>
                                    <span style={{ marginLeft: '16px', fontStyle: 'italic', color: '#666' }}>
                                        Load Negative Test:
                                    </span>
                                    <Link
                                        sx={{ color: '#d32f2f', cursor: 'pointer', ml: 1, mr: 1, fontStyle: 'italic' }}
                                        onClick={() => loadNegativeTest1()}
                                        title="Simulates Elavonamericas processor error"
                                    >
                                        Fail1
                                    </Link>
                                    <Link
                                        sx={{ color: '#d32f2f', cursor: 'pointer', ml: 1, mr: 1, fontStyle: 'italic' }}
                                        onClick={() => loadNegativeTest2()}
                                        title="Simulates expired card error"
                                    >
                                        Fail2
                                    </Link>
                                    <Link
                                        sx={{ color: '#d32f2f', cursor: 'pointer', ml: 1, mr: 1, fontStyle: 'italic' }}
                                        onClick={() => loadNegativeTest3()}
                                        title="Simulates general card decline error"
                                    >
                                        Fail3
                                    </Link>
                                    <Link
                                        sx={{ color: '#d32f2f', cursor: 'pointer', ml: 1, mr: 1, fontStyle: 'italic' }}
                                        onClick={() => loadNegativeTest4()}
                                        title="Simulates Curl Error 6: generic connection error - Could not resolve host: api-pms.storage360.app"
                                    >
                                        Fail4
                                    </Link>
                                    <Link
                                        sx={{ color: '#d32f2f', cursor: 'pointer', ml: 1, mr: 1, fontStyle: 'italic' }}
                                        onClick={() => loadNegativeTest5()}
                                        title="Simulates  Curl Error 56: Recv failure: Connection reset by peer "
                                    >
                                        Fail5
                                    </Link>
                                    <Link
                                        sx={{ color: '#d32f2f', cursor: 'pointer', ml: 1, mr: 1, fontStyle: 'italic' }}
                                        onClick={() => loadNegativeTest6()}
                                        title="Simulates Error processing payment: Soft Decline - The authorization request was approved by the issuing bank but declined by CyberSource because it did not pass the Address Verification Service (AVS) check"
                                    >
                                        Fail6
                                    </Link>
                                </>
                            )}
                        </Box>
                    </Box>
                </Box>
            )}

            {!iscredit && (
                <Box my={2}>
                    <Stack direction="row" spacing={2} justifyContent="center" useFlexGap>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={saveReservation}
                            disabled={loading}
                        >
                            {loading ? <CircularProgress size={24} color="inherit" /> : 'Submit'}
                        </Button>
                    </Stack>
                </Box>
            )}
        </>
    );

};

export default TakePaymentAndRent;
